import EventEmitter from 'events';

import Api from 'Api/Api';
import { ApiErrorResult } from 'Api/ApiErrors';
import ClientStorage from 'Browser/ClientStorage';

import { ConfAppController } from 'WebCall/ConfAppController';
import { isWebRtcSupported } from 'WebCall/BrowserSupport';

import appErrorHandler from 'appErrorHandler';

import { FormController } from './FormController';

import s from './strings';

let enumVal = 0;
export const LINK_STATE_INACTIVE = enumVal++;
export const LINK_STATE_PENDING = enumVal++;
export const LINK_STATE_COLLECT_INFO = enumVal++;
export const LINK_STATE_CHOOSE_CONNECT_METHOD = enumVal++;
export const LINK_STATE_COLLECT_PHONE_NUMBER = enumVal++;
export const LINK_STATE_WEBCALL = enumVal++;
export const LINK_STATE_CONNECTING = enumVal++;
export const LINK_STATE_CONNECTED = enumVal++;
export const LINK_STATE_REGISTER_COMPLETE = enumVal++;

export const COUNTRY_CODE_FALLBACK = 'US';

const POLL_TIMEOUT = 2000;

const INFO_FORM_KEY = 'cl_infoFormValues';
const NUMBER_FORM_KEY = 'cl_numberFormValues';

function loadImage(url, alt) {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.setAttribute('alt', alt || '');
    img.setAttribute('src', url);

    if (img.complete) {
      resolve(img);
      return;
    }

    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => resolve(null));
  });
}

function getClientStorageObject(key) {
  const ret = ClientStorage.readJSON(key);

  if (!(ret && typeof ret === 'object'))
    return {};

  return ret;
}

function getStoredInfoForm() {
  return getClientStorageObject(INFO_FORM_KEY);
}

function getStoredNumberForm() {
  return getClientStorageObject(NUMBER_FORM_KEY);
}

function createFormControllerErrors(err) {
  return err.errorResult.details.invalidFields.map(({ fieldName }) => ({
    fieldName,
    errorType: 'invalid',
  }));
}

export class LinkController extends EventEmitter {
  constructor({ clientCountryCode, conferenceLink, settings }) {
    super();

    this._clientCountryCode = clientCountryCode;
    this._conferenceLink = conferenceLink;
    this._fields = this._conferenceLink.fields || [];

    this._fields.forEach(field => {
      const { required } = field;

      field.rules = {
        required,
        ...(field.type === 'email'
          ? { email: true }
          : {}
        ),
      };
    });

    this.userInfoForm = new FormController(this._fields);
    this.userInfoForm.on('submit', data => this._collectInfoSubmit(data));

    this.phoneNumberForm = new FormController([
      {
        name: 'phoneNumber',
        label: s.phoneNumberLabel,
        rules: {
          required: true,
        },
      }
    ]);
    this.phoneNumberForm.on('submit', data => this._callMe(data));

    this._logoHeaderConfig = this._conferenceLink.images && this._conferenceLink.images.logoHeader;
    this._logoFooterConfig = this._conferenceLink.images && this._conferenceLink.images.logoFooter;
    this._logoHeader = null;
    this._logoFooter = null;

    this._isBusy = false;
    this._userInfo = null;
    this._errorCode = null;
    this._callStatusToken = null;

    this.webCallAppController = null;
    if (settings.WEBCALL_SIP_URI &&
      conferenceLink.webCallToUriPrefix &&
      conferenceLink.webCallToUriSuffix &&
      isWebRtcSupported() &&
      conferenceLink.enableWebCall
    ) {
      this.webCallAppController = new ConfAppController({});

      this.webCallAppController.on('update', () => this.emit('update'));

      this._webCallConnectParams = {
        wsSipURI: settings.WEBCALL_SIP_URI,
        fromSipURI: 'sip:caller@invalid',

        fetchLocationURL: settings.WEBCALL_LOCATION_URL || null,
        fetchRTCConfigurationURL: settings.WEBCALL_RTC_CONFIGURATION_URL || null,
      };
    }

    this._registerData = null;

    const storedInfoForm = getStoredInfoForm();
    this._fields.forEach(field => {
      if (field.name in storedInfoForm && typeof storedInfoForm[field.name] === 'string') {
        field.initialValue = storedInfoForm[field.name];
      }
      if (field.initialValue === null) {
        field.initialValue = '';
      }
    });

    this._storedPhoneNumber = '';
    const storedNumberForm = getStoredNumberForm();
    if (storedNumberForm.phoneNumber && typeof storedNumberForm.phoneNumber === 'string')
      this._storedPhoneNumber = storedNumberForm.phoneNumber;

    if (this._conferenceLink.isActive) {
      if (this._getConnectMethodCount()) {
        this._collectInfoStart();
      } else {
        // go to INACTIVE if no connect methods are configured
        this._changeState(LINK_STATE_INACTIVE);
      }
    } else if (this._conferenceLink.isPending && (this._conferenceLink.enablePreRegister || this.html.preConferenceTitle || this.html.preConferenceBody)) {
      this._changeState(LINK_STATE_PENDING);
    } else {
      this._changeState(LINK_STATE_INACTIVE);
    }
  }

  init() {
    this.emit('update');

    return Promise.all([
      this._logoHeaderConfig
        ? loadImage(this._logoHeaderConfig.url, this._logoHeaderConfig.alt)
        : Promise.resolve(null),
      this._logoFooterConfig
        ? loadImage(this._logoFooterConfig.url, this._logoFooterConfig.alt)
        : Promise.resolve(null),
    ])
      .then(([logoHeader, logoFooter]) => {
        this._logoHeader = logoHeader;
        this._logoFooter = logoFooter;
      });
  }

  initFinish() {
    this.emit('update');
  }

  _collectInfoStart() {
    if (this._fields.length) {
      this._changeState(LINK_STATE_COLLECT_INFO);
    } else {
      // no fields are defined, skip collectInfo
      this._chooseConnectMethodStart();
    }
  }

  _collectInfoSubmit(data) {
    this._userInfo = data;

    const storedInfoForm = getStoredInfoForm();
    this._fields.forEach(field => {
      storedInfoForm[field.name] = this._userInfo[field.name];
    });
    ClientStorage.writeJSON(INFO_FORM_KEY, storedInfoForm);

    if (this._state === LINK_STATE_PENDING) {
      this._registerDialInSubmit();
    } else {
      this._chooseConnectMethodStart();
    }
  }

  _chooseConnectMethodStart() {
    if (this._getConnectMethodCount() > 1) {
      this._changeState(LINK_STATE_CHOOSE_CONNECT_METHOD);
      return;
    }

    if (this.enableWebCall) {
      this._changeState(LINK_STATE_WEBCALL);
    } else if (this.enableDialIn) {
      this._registerDialInSubmit();
    } else if (this.enableDialOut) {
      this._changeState(LINK_STATE_COLLECT_PHONE_NUMBER);
    }
  }

  _getConnectMethodCount() {
    let cnt = 0;
    if (this.enableWebCall) {
      cnt++;
    }
    if (this.enableDialIn) {
      cnt++;
    }
    if (this.enableDialOut) {
      cnt++;
    }
    return cnt;
  }

  _registerDialInSubmit() {
    if (this._isBusy) {
      return;
    }
    this._isBusy = true;
    this.emit('update');

    this._registerData = null;

    const publicID = this._conferenceLink.publicID;

    const params = {
      publicID,
      fieldData: this._userInfo,
    };

    Api.get('ConferenceLink', 'registerDialIn', params, {}, 'authNone')
      .then(data => {
        this._registerData = data.registerDialInResult;
        this._changeState(LINK_STATE_REGISTER_COMPLETE);
      })
      .catch(err => {
        let errorCode;

        if (err instanceof ApiErrorResult) {
          if (err.code === 'ERR_API_FORM_VALIDATION') {
            this.userInfoForm.setErrors(createFormControllerErrors(err));
            this._changeState(LINK_STATE_COLLECT_INFO);
            return;
          }

          if (err.code === 'ERR_API_REQUEST_PARAMETER_INVALID_EXPIRED' && err.parameterName === 'publicID') {
            this._changeState(LINK_STATE_INACTIVE);
            return;
          }
        }

        if (!errorCode) {
          errorCode = appErrorHandler(err);
        }

        this._setError(errorCode);
      })
      .finally(() => {
        this._isBusy = false;
        this.emit('update');
      });
  }

  chooseDialIn() {
    this._registerDialInSubmit();
  }

  chooseWebCall() {
    this._changeState(LINK_STATE_WEBCALL);
  }

  webCallConnect() {
    const publicID = this._conferenceLink.publicID;

    const params = {
      publicID,
      fieldData: this._userInfo,
    };

    Api.get('ConferenceLink', 'registerWebCall', params, {}, 'authNone')
      .then(data => this.webCallAppController.connect({
        ...this._webCallConnectParams,
        ...data.webCallConnectParams,
      }))
      .catch(err => {
        let errorCode;

        if (err instanceof ApiErrorResult) {
          if (err.code === 'ERR_API_FORM_VALIDATION') {
            this.userInfoForm.setErrors(createFormControllerErrors(err));
            this._changeState(LINK_STATE_COLLECT_INFO);
            return;
          }

          if (err.code === 'ERR_API_REQUEST_PARAMETER_INVALID_EXPIRED' && err.parameterName === 'publicID') {
            this._changeState(LINK_STATE_INACTIVE);
            return;
          }
        }

        if (!errorCode) {
          errorCode = appErrorHandler(err);
        }

        this._setError(errorCode);
      });
  }

  webCallSettings() {
    this.webCallAppController.openSettings();
  }

  collectPhoneNumber() {
    this._changeState(LINK_STATE_COLLECT_PHONE_NUMBER);
  }

  _callMe(data) {
    if (this._isBusy) {
      return;
    }
    this._isBusy = true;
    this.emit('update');

    this._callStatusToken = null;

    const { phoneNumber } = data;

    const publicID = this._conferenceLink.publicID;

    const params = {
      publicID,
      number: phoneNumber,
      fieldData: this._userInfo,
    };

    Api.get('ConferenceLink', 'callMe', params, {}, 'authNone')
      .then(data => {
        const storedNumberForm = getStoredNumberForm();
        storedNumberForm.phoneNumber = phoneNumber;
        ClientStorage.writeJSON(NUMBER_FORM_KEY, storedNumberForm);

        this._callStatusToken = data.callMeResult.callStatusToken;
        this._changeState(LINK_STATE_CONNECTING);

        this._pollCallStatus();
      })
      .catch(err => {
        let errorCode;

        if (err instanceof ApiErrorResult) {
          if (err.code === 'ERR_API_FORM_VALIDATION') {
            this.userInfoForm.setErrors(createFormControllerErrors(err));
            this._changeState(LINK_STATE_COLLECT_INFO);
            return;
          }

          if (err.code === 'ERR_API_REQUEST_PARAMETER_INVALID_EXPIRED' && err.parameterName === 'publicID') {
            this._changeState(LINK_STATE_INACTIVE);
            return;
          }

          if (err.isInvalidParamError() && err.parameterName === 'number') {
            this.phoneNumberForm.setErrors([
              {
                fieldName: 'phoneNumber',
                errorType: 'required',
              }
            ]);
            return;
          }
        }

        if (!errorCode) {
          errorCode = appErrorHandler(err);
        }

        this._setError(errorCode);
      })
      .finally(() => {
        this._isBusy = false;
        this.emit('update');
      });
  }

  _pollCallStatus() {
    const params = {
      callStatusToken: this._callStatusToken,
    };

    Api.get('ConferenceLink', 'getCallStatus', params, {}, 'authNone')
      .then(data => {
        switch (data.callStatus.status) {
        case 'success':
          this._changeState(LINK_STATE_CONNECTED);
          break;

        case 'calling':
          setTimeout(() => this._pollCallStatus(), POLL_TIMEOUT);
          break;

        default:
          this._changeState(LINK_STATE_COLLECT_PHONE_NUMBER, 'ERR_CALL_STATUS_FAILED');
          break;
        }
      })
      .catch(err => {
        let errorCode;

        if (err instanceof ApiErrorResult) {
          if (err.isInvalidParamError() && err.parameterName === 'callStatusToken')
            errorCode = 'ERR_CALL_STATUS_FAILED';
        }

        if (!errorCode) {
          errorCode = appErrorHandler(err);
        }

        this._changeState(LINK_STATE_COLLECT_PHONE_NUMBER, errorCode);
      });
  }

  _getBackAction() {
    switch (this._state) {
    case LINK_STATE_CHOOSE_CONNECT_METHOD:
      if (this._fields.length > 0) {
        return () => this._collectInfoStart();
      }
      return null;

    case LINK_STATE_COLLECT_PHONE_NUMBER:
    case LINK_STATE_WEBCALL:
      return () => {
        if (this._getConnectMethodCount() > 1) {
          this._chooseConnectMethodStart();
        } else {
          this._collectInfoStart();
        }
      };
    }

    return null;
  }

  back() {
    const backAction = this._getBackAction();

    if (backAction !== null) {
      backAction();
    }
  }

  get isBackAvailable() {
    return this._getBackAction() !== null;
  }

  _changeState(newState, errorCode = null) {
    this._state = newState;
    this._errorCode = errorCode;
    this.emit('update');
  }

  _setError(errorCode) {
    this._changeState(this._state, errorCode);
  }

  get state() {
    return this._state;
  }

  get isBusy() {
    return this._isBusy;
  }

  get hasStatusMessage() {
    return [
      LINK_STATE_INACTIVE,
      LINK_STATE_CONNECTING,
      LINK_STATE_CONNECTED,
    ].includes(this._state);
  }

  get isWelcomeMessageVisible() {
    return [
      LINK_STATE_PENDING,
      LINK_STATE_COLLECT_INFO,
      LINK_STATE_CHOOSE_CONNECT_METHOD,
      LINK_STATE_COLLECT_PHONE_NUMBER,
      LINK_STATE_WEBCALL,
    ].includes(this._state);
  }

  get isUserInfoFormVisible() {
    return !!(this._state === LINK_STATE_COLLECT_INFO || (this._state === LINK_STATE_PENDING && this._conferenceLink.enablePreRegister));
  }

  get linkName() {
    return this._conferenceLink.name;
  }

  get linkName2() {
    return this._conferenceLink.name2;
  }

  get html() {
    return this._conferenceLink.html;
  }

  get inactiveMessage() {
    return this._conferenceLink.strings?.inactiveMessage;
  }

  get fields() {
    return this._fields;
  }

  get logoHeader() {
    return this._logoHeader;
  }

  get logoFooter() {
    return this._logoFooter;
  }

  get errorCode() {
    return this._errorCode;
  }

  get storedPhoneNumber() {
    return this._storedPhoneNumber;
  }

  get countryCode() {
    return this._clientCountryCode;
  }

  get countryCodeFallback() {
    return COUNTRY_CODE_FALLBACK;
  }

  get registerData() {
    return this._registerData;
  }

  get localTimezoneLabel() {
    return this._conferenceLink.localTimezoneLabel;
  }

  get localStartDate() {
    return this._conferenceLink.localStartDate;
  }

  get localStartTime() {
    return this._conferenceLink.localStartTime;
  }

  get durationLabel() {
    const durationMinutes = Math.floor(this._conferenceLink.duration / 60);

    const hrs = Math.floor(durationMinutes / 60);
    const mins = durationMinutes % 60;

    let result = '';

    if (hrs > 0) {
      result += hrs;
      result += hrs === 1 ? ' hour' : ' hours';
    }

    if (mins > 0) {
      if (result !== '') {
        result += ' and ';
      }
      result += mins + ' minutes';
    }

    if (result === '') {
      result = '0 minutes';
    }

    return result;
  }

  get organizerEmail() {
    return this._conferenceLink.organizerEmail ?? null;
  }

  get enableWebCall() {
    return !!this.webCallAppController;
  }

  get enableDialIn() {
    return this._conferenceLink.enableDialIn;
  }

  get enableDialOut() {
    return this._conferenceLink.enableDialOut;
  }

  get isWebCallBusy() {
    return this.webCallAppController?.isActive ?? false;
  }

  get configErrors() {
    return this._conferenceLink.configErrors;
  }
}
